import { trackSiteVisit, trackSiteVisitTime } from "@/lib/analytics";

// Visitas por país (métricas internas, /admin/estadisticas): una visita por
// pestaña y el tiempo que la página estuvo VISIBLE, enviado al ocultarla o
// cerrarla. Anónimo y sin cookies: el país lo pone Cloudflare en el server y
// acá solo queda en sessionStorage la marca "esta pestaña ya se contó"
// (no identifica a nadie), por eso se mide siempre, como las visitas de GA4.
const COUNTED_KEY = "cv_visit_counted";

// Nuevas vs recurrentes: este navegador guarda solo la fecha (día local) de
// su primera visita y el server recibe "ya había venido otro día" sí/no.
// Ningún identificador sale del navegador.
const FIRST_SEEN_KEY = "cv_first_seen";

function localDay(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// undefined = almacenamiento bloqueado (no se sabe).
function isReturning(): boolean | undefined {
  try {
    const today = localDay();
    const first = localStorage.getItem(FIRST_SEEN_KEY);
    if (!first) {
      localStorage.setItem(FIRST_SEEN_KEY, today);
      return false;
    }
    return first < today;
  } catch {
    return undefined;
  }
}

// Tras tanto rato oculta, volver a la pestaña es una visita nueva (mismo
// criterio que el server, que deja de aceptar tiempo pasada esta ventana).
const NEW_VISIT_AFTER_MS = 30 * 60 * 1000;

// Menos de esto no aporta al promedio (pestañas que se abren y cierran).
const MIN_SEND_MS = 1000;

function alreadyCounted(): boolean {
  try {
    if (sessionStorage.getItem(COUNTED_KEY)) return true;
    sessionStorage.setItem(COUNTED_KEY, "1");
  } catch {
    // Almacenamiento bloqueado: se cuenta por carga de página.
  }
  return false;
}

// Devuelve la función que deja de medir (cleanup de useEffect).
export function startSiteVisitTracking(): () => void {
  if (!alreadyCounted()) trackSiteVisit(isReturning());

  let visibleSince: number | null = document.visibilityState === "visible" ? Date.now() : null;
  let hiddenAt: number | null = visibleSince === null ? Date.now() : null;

  function flush(): void {
    if (visibleSince === null) return;
    const ms = Date.now() - visibleSince;
    visibleSince = null;
    hiddenAt = Date.now();
    if (ms >= MIN_SEND_MS) trackSiteVisitTime(ms);
  }

  function onVisibilityChange(): void {
    if (document.visibilityState === "hidden") {
      flush();
      return;
    }
    if (visibleSince !== null) return;
    if (hiddenAt !== null && Date.now() - hiddenAt >= NEW_VISIT_AFTER_MS) trackSiteVisit(isReturning());
    visibleSince = Date.now();
    hiddenAt = null;
  }

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", flush);
  return () => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", flush);
    flush();
  };
}
